/*
  The joiners / leavers / movers refresh, ported from applyDataUpdate (orgchart.html:10339–10451).

  This is the riskiest function in the whole port, and not because it is complicated. It is risky because
  every way it goes wrong goes wrong SILENTLY: a refresh that re-parents the entire reporting hierarchy,
  zeroes every salary, or leaves a departed person heading a team produces no error and no warning, just
  numbers that are quietly different from the ones the consultant last agreed with the client.

  So the ordering below is not stylistic. Four constraints, each of which is a defect if broken:

    1. The population is written BEFORE any decision is reconciled. previewPerimeterThreshold classifies
       against the roster it is handed and skips leavers, so resolving a rule before the roster lands makes
       joiners invisible to it while departed people remain eligible. Every write still succeeds; only the
       membership is wrong. (orgchart.html:10364 before :10369.)
    2. A returning leaver's stale 'No longer required' decision is cleared BEFORE the joiner default and
       before the rule runs, and cleared by DELETING the row rather than by writing 'Remain'. The rule
       treats an explicit NLR as a human's choice and refuses to move it, so the wrong order leaves the
       person permanently outside the perimeter — again with no error.
    3. A rule exit deletes the decision row rather than writing 'Remain'. The two read identically
       everywhere else, but an explicit Remain is counted as a manual choice the rule may never touch, so
       writing it would disqualify the person from every future threshold change.
    4. Nobody is ever deleted. A leaver keeps their row and gains a flag: their severance still prices,
       they still anchor the audit trail, and — the part that is easy to miss — keeping the row is what
       stops a head designation from pointing at somebody who no longer exists.

  Six things the original does that this deliberately does not, each recorded rather than inherited:

    · It reconciles buckets in every scenario but repairs HEADS only in the active one, so every other
      scenario keeps designations pointing at departed people. Here every scenario is repaired, and by one
      predicate — the original uses two that disagree (`===` in removeHeadEverywhere, String-set
      membership at :10401), so its own preview under-reports what its apply clears.
    · It silently DELETES anybody already flagged as a leaver by a previous refresh, because they are
      excluded from the old-side identity map and absent from the new file. Leaver retention there survives
      exactly one update cycle. Here a previously-flagged leaver is left alone.
    · It accepts a partial file: only name and id are validated as present, so uploading a headcount-only
      extract zeroes every salary and FTE and flattens the hierarchy into one blank group. Here a mapped
      column missing from the file is refused.
    · Its active and non-active scenario paths are hand-mirrored rather than shared, and they have drifted:
      the non-active path leaves a rule entrant's stranded plan in place, which the invariant says cannot
      exist. Here one path serves every scenario, so they cannot drift.
    · It calls seedUndoBaseline at the end, which discards the whole undo history — making every EARLIER
      design step unrevertable too. Here the refresh is atomic and fully audited; see the note on the
      inverse below.
    · It applies supervisorId, legalEntity and country while its diff reports none of them. Here they are
      detected, because a refresh that re-parents the reporting hierarchy with no audit trail is not a
      thing a consultant can defend in a room.
*/
import { DEFAULT_BUCKET, } from '@integrate/contracts';
import { CommandError } from '../errors.js';
import { COLUMN_KEYS, isLevelKey } from '../fields.js';
import { employeesFromRows } from '../project-file.js';
import { previewPerimeterThreshold } from '../analytics.js';
import { bucketPatch, decisionWrite, thresholdState } from './decisions.js';
import { required, } from './types.js';
const EMPTY_DECISION = {
    status: null,
    statusSrc: null,
    joinStage: null,
    day2Exit: null,
    strandedAction: null,
    strandedMonth: null,
};
/** The fields a refresh compares, matching the extended DIFF_ATTRIBUTES in diagnostics. */
const COMPARED = [
    'name', 'salary', 'fte', 'dedication', 'openPosition', 'inactive',
    'supervisorId', 'jobTitle', 'location', 'country', 'seniority', 'businessUnit', 'legalEntity',
    'currency', 'pensionScheme',
];
/** String-compares, so 0 and '0' and a null and a '' are the same absence they are on a spreadsheet. */
function same(a, b) {
    return String(a ?? '') === String(b ?? '');
}
/**
 * What a staged file implies for the current roster.
 *
 * Exported so the preview endpoint and the apply share one implementation. The original computes this
 * once at preview time and applies the frozen copy; on a server the roster can move in between, so it is
 * recomputed here and the preview is advisory. That is the whole reason the command names a run rather
 * than carrying a diff.
 */
export function diffRoster(current, incoming, levelKeys) {
    const incomingById = new Map(incoming.map((row) => [row.id, row]));
    const diff = { joiners: [], leaverIds: [], movers: [], unchanged: 0, stillDeparted: 0 };
    for (const row of incoming) {
        const existing = current[row.id];
        if (!existing || existing.isLeaver) {
            // Absent, or present but departed — a returning leaver is a joiner, which is what clears their
            // stale 'No longer required' and lets the rule consider them again.
            diff.joiners.push(row);
            continue;
        }
        const changes = [];
        for (const field of COMPARED) {
            const from = existing[field];
            const to = row[field];
            if (!same(from, to))
                changes.push({ field, from, to });
        }
        for (const key of levelKeys) {
            const from = existing.levels?.[key];
            const to = row.levels[key];
            if (!same(from, to))
                changes.push({ field: key, from, to });
        }
        if (changes.length)
            diff.movers.push({ employeeId: row.id, name: row.name, changes });
        else
            diff.unchanged++;
    }
    for (const [id, person] of Object.entries(current)) {
        if (incomingById.has(id))
            continue;
        if (person.isLeaver) {
            diff.stillDeparted++;
            continue;
        }
        diff.leaverIds.push(id);
    }
    return diff;
}
/**
 * Carry in-app dedication forward when the file cannot supply it.
 *
 * Ported from handleUpdateFile (orgchart.html:10216–10225) and NOT optional. Dedication is editable in the
 * side panel and a great many HR extracts have no column for it at all — the diagnostics surface says so
 * and tells the user to type the values in — so without this every refresh silently blanks every
 * dedication the consultant entered. That is not merely lost typing: dedication is what the perimeter rule
 * keys off, so the next re-run of the rule evicts every member it had designated, and the carve-out
 * quietly empties out with no error anywhere.
 *
 * Two rules, both the original's. A mapped column in the file always wins, even when its cell is blank —
 * that is how "no longer shared" is expressed. And a leaver is excluded: somebody returning after a
 * departure starts from the fresh extract rather than from their stale pre-departure dedication.
 */
export function carryForwardDedication(current, incoming, fileSuppliesDedication) {
    if (fileSuppliesDedication)
        return [...incoming];
    return incoming.map((row) => {
        if (row.dedication !== null)
            return row;
        const existing = current[row.id];
        if (!existing || existing.isLeaver)
            return row;
        /*
          The legacy marker rides with the number it stands in for. It is the SAME cell — a file writes a
          value or the word "Shared" there, never both — so an extract that omits the column must carry both
          halves forward or an existing split-head person is silently un-marked by a refresh that never
          mentioned dedication at all.
        */
        return { ...row, dedication: existing.dedication, legacyShared: existing.legacyShared === true };
    });
}
/**
 * Which columns the project maps but the file does not have.
 *
 * The original validates only name and id (orgchart.html:10206), so a headcount-only extract coerces
 * every salary and FTE to 0 and every grouping level to blank — worse than leaving a field unmapped, which
 * at least defaults FTE to 1. Refused rather than warned about: there is no reading of "I meant to zero
 * every salary" that a warning serves.
 */
export function missingMappedColumns(columnMap, headers) {
    const present = new Set(headers);
    const missing = [];
    for (const [key, header] of Object.entries(columnMap)) {
        if (!header)
            continue;
        // Only the fields that become a person; an unmapped key is not the file's problem.
        if (!COLUMN_KEYS.includes(key) && !isLevelKey(key))
            continue;
        if (!present.has(header))
            missing.push(header);
    }
    return missing;
}
/** Whether the file actually carries the column this logical field is mapped to. */
export function fileSupplies(columnMap, headers, key) {
    const header = columnMap[key];
    return !!header && headers.includes(header);
}
/** Equal as mappings, ignoring key order and blank entries. */
function sameMapping(a, b) {
    const norm = (m) => Object.entries(m)
        .filter(([, v]) => !!v)
        .sort(([x], [y]) => x.localeCompare(y))
        .map(([k, v]) => `${k}=${v}`)
        .join('|');
    return norm(a) === norm(b);
}
/** The roster as it will be once the run is applied — what every reconcile classifies against. */
function rosterAfter(current, diff, incoming) {
    const after = Object.create(null);
    for (const [id, person] of Object.entries(current))
        after[id] = { ...person };
    for (const row of incoming) {
        after[row.id] = {
            id: row.id,
            name: row.name,
            dedication: row.dedication,
            fte: row.fte,
            salary: row.salary,
            openPosition: row.openPosition,
            inactive: row.inactive,
            // Everybody in the file is present, which is what un-flags a returning leaver.
            isLeaver: false,
            supervisorId: row.supervisorId,
            jobTitle: row.jobTitle,
            location: row.location,
            country: row.country,
            currency: row.currency,
            pensionScheme: row.pensionScheme,
            seniority: row.seniority,
            businessUnit: row.businessUnit,
            legalEntity: row.legalEntity,
            levels: row.levels,
            isBuildUp: false,
        };
    }
    for (const id of diff.leaverIds) {
        const person = after[id];
        if (person)
            person.isLeaver = true;
    }
    return after;
}
/**
 * Reconcile one scenario's decisions against the refreshed population.
 *
 * The same code for every scenario. The original hand-mirrors an active path and a non-active path, and
 * they have drifted — the non-active one leaves a rule entrant's stranded plan behind, which the
 * invariant says cannot exist. One path cannot drift from itself.
 */
function reconcileScenario(scenarioId, threshold, decisions, diff, after) {
    const writes = [];
    const touched = [];
    // A working copy, so the rule below sees the decisions this function has just made.
    const live = Object.create(null);
    for (const [id, row] of Object.entries(decisions))
        live[id] = { ...row };
    let marked = 0;
    let reset = 0;
    /*
      Departures. `bucketPatch` clears the stage attribution and the stranded plan with the bucket,
      which is the original's behaviour and is also the invariant: attrition beats a mitigation plan.
  
      AND THE CLEARED PLAN IS WRITTEN DOWN FIRST, which is new and is the whole of what makes
      plan-versus-actual possible. The invariant above is right about the LIVE design — a departed
      person strands nothing and should carry no plan — and it had a consequence nobody chose: the
      plan and the departure are destroyed and created in the same transaction, so by the time a
      person has an actual, the plan they had is gone. `applyDataUpdate` records no inverse either, so
      there was nowhere left to recover it from. The ledger changes nothing about what the design
      says; it records what it used to say, once, at the only moment anything knows both.
    */
    const departures = [];
    for (const id of diff.leaverIds) {
        const previous = live[id] ?? EMPTY_DECISION;
        if (previous.status === 'No longer required' && previous.strandedAction === null)
            continue;
        departures.push({
            employeeId: id,
            plannedAction: previous.strandedAction,
            // Null together, always: a month without an action is a month for nothing.
            plannedMonth: previous.strandedAction === null ? null : previous.strandedMonth,
            plannedDay2Exit: previous.status === 'Carve-out' && previous.day2Exit === true,
        });
        const patch = bucketPatch(previous, 'No longer required');
        writes.push(decisionWrite(scenarioId, id, previous, patch));
        live[id] = { ...EMPTY_DECISION, status: 'No longer required' };
        touched.push(`bucket:${id}`);
        marked++;
    }
    if (departures.length)
        writes.push({ t: 'departure', scenarioId, rows: departures });
    /*
      Arrivals, in two steps that must not be reordered.
  
      First clear a stale 'No longer required' — a joiner carrying one can only be a returning leaver, since
      anybody marked NLR while still in the data is never classified a joiner. Without the clear they stay
      NLR for ever: the default below skips them because a bucket is set, and the rule refuses to move what
      it reads as a human's decision.
    */
    for (const row of diff.joiners) {
        const previous = live[row.id] ?? EMPTY_DECISION;
        if (previous.status === 'No longer required') {
            writes.push({ t: 'decision', scenarioId, employeeId: row.id, patch: null });
            live[row.id] = { ...EMPTY_DECISION };
            touched.push(`bucket:${row.id}`);
            reset++;
        }
    }
    /*
      Then the default — but only when this scenario has no rule. With a rule active a joiner is left with
      NO decision at all, which reads as Remain but stays rule-ELIGIBLE, so the rule below can pull them in.
      Writing an explicit 'Remain' instead would count as a human's choice and freeze them out of every
      future threshold change. The condition is per scenario, from that scenario's own threshold.
    */
    if (threshold === null) {
        for (const row of diff.joiners) {
            const previous = live[row.id] ?? EMPTY_DECISION;
            if (previous.status)
                continue;
            const patch = bucketPatch(previous, DEFAULT_BUCKET);
            writes.push(decisionWrite(scenarioId, row.id, previous, patch));
            live[row.id] = { ...EMPTY_DECISION, status: DEFAULT_BUCKET, statusSrc: 'manual' };
            touched.push(`bucket:${row.id}`);
        }
    }
    // Re-run this scenario's rule over the refreshed population: joiners who qualify enter, and people
    // whose dedication dropped fall out. Manual decisions are untouched — that is the slider's contract.
    let ruleIn = 0;
    let ruleOut = 0;
    if (threshold !== null) {
        const preview = previewPerimeterThreshold(thresholdState(after, live), threshold);
        for (const id of preview.enterIds) {
            const previous = live[id] ?? EMPTY_DECISION;
            writes.push(decisionWrite(scenarioId, id, previous, bucketPatch(previous, 'Carve-out', { src: 'rule', joinStage: 'perimeter' })));
            touched.push(`bucket:${id}`);
            ruleIn++;
        }
        for (const id of preview.exitIds) {
            const previous = live[id] ?? EMPTY_DECISION;
            // Back to undecided, not to Remain — see constraint 3 at the top of this file.
            writes.push(decisionWrite(scenarioId, id, previous, bucketPatch(previous, null)));
            touched.push(`bucket:${id}`);
            ruleOut++;
        }
    }
    return { writes, touched, marked, reset, ruleIn, ruleOut };
}
/**
 * Head designations whose holder is no longer anywhere, in EVERY scenario.
 *
 * One predicate: a designation survives if its holder is in the refreshed roster and NOT departed, or is
 * a planned hire in that scenario. A leaver keeps their employee row — that is what makes their severance
 * price and their history resolve — so "still has a row" is not the question; "is still working here" is.
 *
 * The original uses two predicates that disagree, and they are BOTH on the refresh path: the preview tests
 * `!newById.has(id) && oldEmpIds.has(id)` (orgchart.html:10274, where oldEmpIds excludes anybody already
 * flagged as departed) while the apply tests `!validHeadIds.has(id)` (:10401, the new file plus build-ups).
 * So the apply clears heads the preview never warned about — a head held by an already-departed person, or
 * by a stale id, is cleared silently and the count in the toast can exceed the count on screen.
 * (removeHeadEverywhere at :2646 uses a third comparison but is not called from here.)
 *
 * It also repairs only the ACTIVE scenario, so every other one keeps designations pointing at departed
 * people, which then export and silently resurrect if the id ever comes back.
 *
 * An explicit "no head" marker — a present row with a null holder — is never touched. It is a decision,
 * not an absence, and clearing it would let a Remain view inherit the as-is head somebody removed.
 */
function repairHeads(allHeads, allBuildUpIds, after) {
    const writes = [];
    const touched = [];
    let cleared = 0;
    for (const [scenarioId, byPerspective] of Object.entries(allHeads)) {
        /*
          Null-prototype fallback. Ids come from a client spreadsheet and only __proto__, prototype and
          constructor are screened on the way in, so an id of `valueOf` or `hasOwnProperty` is a real employee
          id — and against a plain `{}` it would test truthy, keeping a departed person's head designation.
        */
        const hires = allBuildUpIds[scenarioId] ?? Object.create(null);
        for (const [perspective, heads] of Object.entries(byPerspective)) {
            for (const [groupId, employeeId] of Object.entries(heads)) {
                if (employeeId === null)
                    continue;
                const holder = after[employeeId];
                if ((holder && !holder.isLeaver) || hires[employeeId])
                    continue;
                writes.push({
                    t: 'head',
                    scenarioId,
                    perspective: perspective,
                    groupId,
                    employeeId: null,
                    present: false,
                });
                /*
                  THE THREE-PART SHAPE the rest of this namespace uses. It recorded a fourth segment for the
                  scenario, which no reader parses: `touchesNode` takes the whole remainder after the first colon
                  as the group id, and `setHead`/`clearHead` write `head:<perspective>:<groupId>`. So a refresh
                  that cleared the head of the group a colleague had open removed it from their panel in silence,
                  while an ordinary head change on the same group notified — and the revert guard could not see
                  the two as touching the same thing either. The scenario is not the key's business: it is on the
                  event, and that is what the guard scopes by.
                */
                touched.push(`head:${perspective}:${groupId}`);
                cleared++;
            }
        }
    }
    return { writes, touched, cleared };
}
/**
 * Target placements belonging to somebody who has gone.
 *
 * The original leaves them for ever, so they resurrect if the id is ever reused. Dropped here, in every
 * scenario — and this is the one cross-scenario cleanup the write vocabulary could already express,
 * because a placement IS an override row at perspective 'target'.
 */
function dropPlacements(placements, leaverIds) {
    const writes = [];
    const departed = new Set(leaverIds);
    let dropped = 0;
    for (const [scenarioId, byPerson] of Object.entries(placements)) {
        for (const [employeeId, byLevel] of Object.entries(byPerson)) {
            if (!departed.has(employeeId))
                continue;
            for (const fieldKey of Object.keys(byLevel)) {
                writes.push({
                    t: 'override',
                    scenarioId,
                    perspective: 'target',
                    employeeId,
                    fieldKey,
                    value: undefined,
                });
                dropped++;
            }
        }
    }
    return { writes, dropped };
}
export const applyDataUpdate = {
    needs: [
        'updateRun',
        'employees',
        'settings',
        'project',
        'scenarios',
        'allDecisions',
        'allHeads',
        'allBuildUpIds',
        'targetPlacements',
    ],
    apply(slice, command) {
        const run = required(slice, 'updateRun');
        const settings = required(slice, 'settings');
        const project = required(slice, 'project');
        const current = required(slice, 'employees');
        const scenarios = required(slice, 'scenarios');
        if (run.appliedAt !== null) {
            throw new CommandError('conflict', `Refresh ${run.id} has already been applied`);
        }
        /*
          Refuse a refresh read under a different mapping than the project now has. Re-deriving people from a
          changed mapping would silently re-key the whole hierarchy, and the original's answer to a
          differently-shaped file is a full re-import, not an update.
        */
        if (!sameMapping(run.columnMap, settings.columnMap)) {
            throw new CommandError('conflict', 'The column mapping changed after this file was staged; upload it again');
        }
        const missing = missingMappedColumns(settings.columnMap, run.headers);
        if (missing.length) {
            throw new CommandError('invalid', `The file is missing mapped columns: ${missing.join(', ')}. `
                + 'Applying it would blank those fields for everybody.');
        }
        if (!run.rows.length) {
            // Every single person a leaver is not a refresh, it is a mistake with a spreadsheet.
            throw new CommandError('invalid', 'The staged file has no rows');
        }
        const rosterCount = Object.keys(current).length;
        if (command.expectRosterCount !== undefined && command.expectRosterCount !== rosterCount) {
            throw new CommandError('conflict', `The roster changed since the preview (${command.expectRosterCount} → ${rosterCount}); review it again`);
        }
        const levelKeys = project.groupingLevels.map((l) => l.key);
        /*
          Carried forward BEFORE the diff, so a value the file could not supply does not read as a change — and
          before the rule, which keys off it. The header the run recorded is what decides: the project may map
          a dedication column that this particular extract does not contain.
        */
        const rows = carryForwardDedication(current, run.rows, fileSupplies(settings.columnMap, run.headers, 'dedication'));
        const diff = diffRoster(current, rows, levelKeys);
        const after = rosterAfter(current, diff, rows);
        /*
          Order below is constraint 1: the roster lands first. Every reconcile classifies against `after`, and
          a decision row for somebody who does not exist yet projects as nothing rather than as an error.
        */
        const writes = [{ t: 'employeeUpsert', rows }];
        const touched = [];
        if (diff.leaverIds.length) {
            writes.push({ t: 'employeeLeaver', employeeIds: diff.leaverIds, isLeaver: true });
            for (const id of diff.leaverIds)
                touched.push(`employee:${id}:isLeaver`);
        }
        for (const mover of diff.movers)
            touched.push(`employee:${mover.employeeId}`);
        const allDecisions = required(slice, 'allDecisions');
        let ruleIn = 0;
        let ruleOut = 0;
        let reconciled = 0;
        for (const [scenarioId, scenario] of Object.entries(scenarios)) {
            const result = reconcileScenario(scenarioId, scenario.perimeterThreshold, allDecisions[scenarioId] ?? {}, diff, after);
            if (result.writes.length)
                reconciled++;
            writes.push(...result.writes);
            touched.push(...result.touched);
            ruleIn += result.ruleIn;
            ruleOut += result.ruleOut;
        }
        const heads = repairHeads(required(slice, 'allHeads'), required(slice, 'allBuildUpIds'), after);
        writes.push(...heads.writes);
        touched.push(...heads.touched);
        const placements = dropPlacements(required(slice, 'targetPlacements'), diff.leaverIds);
        writes.push(...placements.writes);
        writes.push({ t: 'updateRunApplied', runId: run.id });
        const parts = [
            `+${diff.joiners.length} joiners`,
            `−${diff.leaverIds.length} leavers`,
            `${diff.movers.length} changed`,
        ];
        if (ruleIn || ruleOut)
            parts.push(`perimeter rule +${ruleIn}/−${ruleOut}`);
        if (heads.cleared)
            parts.push(`${heads.cleared} head designation(s) cleared`);
        if (placements.dropped)
            parts.push(`${placements.dropped} target placement(s) dropped`);
        if (diff.stillDeparted)
            parts.push(`${diff.stillDeparted} still departed`);
        return {
            writes,
            /*
              Deliberately not undoable, and the reason is not "it is hard".
      
              An inverse here is a list of catalogue commands, and a refresh changes the POPULATION: undoing it
              would need a command that deletes an employee row, which does not exist and should not — deleting
              a person destroys their severance basis and their audit trail, which is exactly why a departure is
              a flag and not a delete. Recovering from a bad refresh is what PROJECT point-in-time restore is
              for — the project, because the roster a refresh replaces belongs to the project and not to any
              one scenario, and this command records no scenario id at all.
      
              Even so this is a large improvement on the original, which emitted no reversible entry AND then
              discarded the entire undo history, making every earlier design step unrevertable too. This change
              is atomic, fully audited, and leaves every other event revertable.
            */
            inverse: null,
            summary: `Data update from ${run.filename}: ${parts.join(' · ')}`,
            touched,
        };
    },
};
/* ------------------------------------------------------------------ *
 * Re-deriving the roster from its retained source
 * ------------------------------------------------------------------ */
/**
 * Re-derive every person's fields from their retained source row under the CURRENT column mapping.
 *
 * This exists because `setColumnMapping` writes the mapping and nothing else. Without this command, re-
 * mapping a column leaves a project whose mapping says one thing while every stored value was derived from
 * another, and nothing reconciles them — a silent disagreement between the mapping and the data.
 *
 * It is called `rebuildRosterFromSource` after the original's destructive path (buildEmployeesFromRows,
 * orgchart.html:3584), which deletes every scenario, the entire change log, all heads, buckets, build-ups,
 * overrides and saved views, with no confirmation and no undo. It does not do that, and the reason is worth
 * stating: the destruction there is a consequence of how it was implemented, not of what re-deriving means.
 * Re-deriving from the same rows keeps the same PEOPLE, so
 *
 *   · if the id mapping is unchanged, every identity is stable and the whole design stays valid. Nothing
 *     needs destroying — this is a data correction, and it is treated as one.
 *   · if the id mapping changed, every identity changes, the design describes people who no longer exist,
 *     and this is a re-import rather than a re-map. Refused, and said plainly.
 *
 * It still carries a typed confirmation and stays owner-only, because it rewrites every field of every
 * person in one go even when it destroys nothing.
 *
 * Only fields are re-derived: `isLeaver` and the retained row itself are untouched, so a departure survives
 * a re-mapping. That is why this emits patches rather than a whole-row upsert.
 */
export const rebuildRosterFromSource = {
    needs: ['project', 'settings', 'employees', 'employeeRaw'],
    apply(slice, command) {
        const project = required(slice, 'project');
        const settings = required(slice, 'settings');
        const current = required(slice, 'employees');
        const rawById = required(slice, 'employeeRaw');
        if (command.confirmProjectName !== project.name) {
            throw new CommandError('invalid', 'Type the project name exactly to confirm re-deriving the whole roster');
        }
        const retained = Object.keys(rawById);
        if (!retained.length) {
            /*
              Nothing to re-derive from. Source rows are kept only when the uploader opts in, and that defaults to
              off precisely because an unmapped column can carry a home address or a bank account. Refusing is the
              honest outcome: silently producing an unchanged roster would let somebody believe a mapping fix had
              been applied when it had not.
            */
            throw new CommandError('conflict', 'This project did not retain its source rows, so there is nothing to re-derive from. '
                + 'Import the file again, or stage it as a data update.');
        }
        // Deterministic order, so a duplicate id resolves the same way every time it is run.
        const ordered = retained.sort();
        const derived = employeesFromRows(ordered.map((id) => rawById[id]), { columnMap: settings.columnMap, groupingLevels: project.groupingLevels });
        /*
          Identity has to be stable. employeesFromRows derives the id from the mapping and drops a row whose id
          is a duplicate or a forbidden object key, so comparing the derived set against the retained set catches
          an id re-mapping, a collision and a dropped row in one check — each of which means this is no longer a
          re-derivation of the same people.
        */
        const derivedIds = new Set(derived.map((row) => row.id));
        const missing = ordered.filter((id) => !derivedIds.has(id));
        if (missing.length || derivedIds.size !== ordered.length) {
            throw new CommandError('conflict', 'Re-deriving under this mapping would change who the people are, so it is an import rather than a '
                + 're-map. Check the id column, and import the file again if it really has changed.');
        }
        const patches = [];
        const touched = [];
        let changedPeople = 0;
        const changedFields = new Set();
        for (const row of derived) {
            const existing = current[row.id];
            // A person with a retained row but no employee row cannot happen — the rows are keyed off employees —
            // but if it ever did, patching a row that does not exist would silently do nothing.
            if (!existing)
                continue;
            const fields = {};
            const levels = {};
            let differs = false;
            for (const field of COMPARED) {
                const to = row[field];
                if (same(existing[field], to))
                    continue;
                fields[field] = to;
                changedFields.add(field);
                differs = true;
            }
            for (const level of project.groupingLevels) {
                const to = row.levels[level.key] ?? null;
                if (same(existing.levels?.[level.key], to))
                    continue;
                levels[level.key] = to;
                changedFields.add(level.key);
                differs = true;
            }
            if (!differs)
                continue;
            patches.push({
                employeeId: row.id,
                ...(Object.keys(fields).length ? { fields } : {}),
                ...(Object.keys(levels).length ? { levels } : {}),
            });
            touched.push(`employee:${row.id}`);
            changedPeople++;
        }
        const summary = changedPeople
            ? `Re-derived the roster from source under the current mapping: `
                + `${changedPeople} of ${ordered.length} people changed `
                + `(${[...changedFields].sort().join(', ')})`
            : `Re-derived the roster from source under the current mapping: nothing changed`;
        return {
            writes: patches.length ? [{ t: 'employeePatch', patches }] : [],
            /*
              No inverse, and for once that costs nothing. The result is a pure function of the retained rows —
              which are immutable — and the column mapping. So the way back is to put the mapping back and run this
              again, which reproduces the previous state exactly. An inverse listing every field of every person
              would be a worse version of the same thing, and would not fit in a batch.
            */
            inverse: null,
            summary,
            touched,
        };
    },
};
//# sourceMappingURL=intake.js.map